/**
 * Blacklist Panel (Schritt „Blacklists", nur Admin)
 *
 * Tabelle aller Blacklists des Workspace: Name, Typ (Firmen/Kontakte),
 * Einträge und Geltung — „Global" (isDefault: gilt automatisch für alle
 * Kampagnen) oder „pro Kampagne wählbar". Neue Blacklists werden über ein
 * Modal angelegt. Der Schritt ist optional und kann übersprungen werden.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Switch,
  Tooltip,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { API_V1 } from '../../../API/APIs/APIv1';
import useAIChatStore from '../../../stores/ai-chat-store';
import { dc } from '../design-tokens';

interface BlacklistPanelProps {
  onSaved: (summary: string) => void;
  /** Schritt überspringen (optional — keine Blacklist nötig). */
  onSkip: () => void;
  skipped: boolean;
  /** Liste angelegt oder Schritt übersprungen: weiter. */
  onNext: () => void;
}

interface BlacklistRow {
  id: string;
  name: string;
  type: 'company' | 'contact';
  itemCount: number;
  isDefault: boolean;
}

const BlacklistPanel: React.FC<BlacklistPanelProps> = ({ onSaved, onSkip, skipped, onNext }) => {
  const { classes, cx } = useStyles();
  const { i18n } = useTranslation();
  const isDe = i18n.language === 'de';
  const dataVersion = useAIChatStore((state) => state.dataVersion);

  const [lists, setLists] = useState<BlacklistRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'company' | 'contact'>('company');
  const [isDefault, setIsDefault] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Aufgeklappte Liste + deren Einträge
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [items, setItems] = useState<Array<{ id: string; label: string }>>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [newEntries, setNewEntries] = useState('');
  const [addingItems, setAddingItems] = useState(false);

  const itemLabel = (it: any) =>
    it.companyName ||
    it.contactVanityName ||
    it.contactLinkedinUrl ||
    it.companyLinkedinUrl ||
    it._id;

  const loadItems = async (blacklistId: string) => {
    setItemsLoading(true);
    try {
      const res = await API_V1.get(`/blacklists/${blacklistId}/items`, { params: { limit: 100 } });
      const raw = res.data?.items || res.data || [];
      setItems(raw.map((it: any) => ({ id: it._id, label: itemLabel(it) })));
    } catch {
      setItems([]);
    } finally {
      setItemsLoading(false);
    }
  };

  const toggleExpand = (row: BlacklistRow) => {
    if (expandedId === row.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(row.id);
    setNewEntries('');
    loadItems(row.id);
  };

  const handleAddEntries = async (row: BlacklistRow) => {
    const lines = newEntries
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length === 0 || addingItems) return;
    setAddingItems(true);
    setError(null);
    try {
      if (row.type === 'company') {
        const payload = lines.map((l) =>
          l.startsWith('http')
            ? { companyName: l.split('/').filter(Boolean).pop() || l, companyLinkedinUrl: l }
            : { companyName: l }
        );
        await API_V1.post(`/blacklists/${row.id}/companies`, { items: payload });
      } else {
        const payload = lines.map((l) =>
          l.startsWith('http') ? { contactLinkedinUrl: l } : { contactVanityName: l }
        );
        await API_V1.post(`/blacklists/${row.id}/contacts`, { items: payload });
      }
      setNewEntries('');
      await loadItems(row.id);
      setLists((prev) =>
        prev.map((l) => (l.id === row.id ? { ...l, itemCount: l.itemCount + lines.length } : l))
      );
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          (isDe
            ? 'Einträge konnten nicht hinzugefügt werden — bei Kontakten bitte LinkedIn-URL oder Profilname angeben.'
            : 'Could not add entries — for contacts, provide a LinkedIn URL or profile name.')
      );
    } finally {
      setAddingItems(false);
    }
  };

  const handleDeleteItem = async (row: BlacklistRow, itemId: string) => {
    try {
      await API_V1.delete(`/blacklists/${row.id}/items`, { data: { itemIds: [itemId] } });
      setItems((prev) => prev.filter((it) => it.id !== itemId));
      setLists((prev) =>
        prev.map((l) => (l.id === row.id ? { ...l, itemCount: Math.max(0, l.itemCount - 1) } : l))
      );
    } catch {
      /* Zeile bleibt stehen */
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API_V1.get('/blacklists');
        if (cancelled) return;
        setLists(
          (res.data?.blacklists || []).map((b: any) => ({
            id: b._id,
            name: b.name,
            type: b.type,
            itemCount: b.itemCount || 0,
            isDefault: !!b.isDefault,
          }))
        );
      } catch {
        if (!cancelled) setLists([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataVersion]);

  const toggleDefault = async (row: BlacklistRow) => {
    const next = !row.isDefault;
    setLists((prev) => prev.map((l) => (l.id === row.id ? { ...l, isDefault: next } : l)));
    try {
      await API_V1.patch(`/blacklists/${row.id}`, { isDefault: next });
    } catch {
      setLists((prev) => prev.map((l) => (l.id === row.id ? { ...l, isDefault: row.isDefault } : l)));
    }
  };

  const canCreate = name.trim().length > 0 && !saving;

  const handleCreate = async () => {
    if (!canCreate) return;
    setSaving(true);
    setError(null);
    try {
      const res = await API_V1.post('/blacklists', {
        name: name.trim(),
        description: description.trim() || undefined,
        type,
        isDefault,
      });
      const b = res.data;
      setLists((prev) => [
        ...prev,
        { id: b._id, name: b.name, type: b.type, itemCount: 0, isDefault: !!b.isDefault },
      ]);
      setModalOpen(false);
      onSaved(
        isDe
          ? `Ich habe die Blacklist „${name.trim()}" (${type === 'company' ? 'Firmen' : 'Kontakte'}, ${isDefault ? 'global' : 'pro Kampagne wählbar'}) angelegt. Bitte hilf mir jetzt, Einträge hinzuzufügen — ich kann dir mehrere auf einmal geben.`
          : `I created the blacklist "${name.trim()}" (${type}, ${isDefault ? 'global' : 'selectable per campaign'}). Please help me add entries — I can give you several at once.`
      );
      setName('');
      setDescription('');
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          (isDe ? 'Anlegen fehlgeschlagen — bitte erneut versuchen.' : 'Creation failed — please try again.')
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {lists.length > 0 || skipped ? (
        <Box className={cx(classes.gapBanner, classes.doneBanner)}>
          <span className={cx(classes.gapDot, classes.doneDot)}>✓</span>
          <Typography variant="caption" className={cx(classes.gapText, classes.doneText)}>
            {lists.length > 0
              ? isDe
                ? `${lists.length} Blacklist(s) im Workspace`
                : `${lists.length} blacklist(s) in the workspace`
              : isDe
                ? 'Übersprungen — du kannst jederzeit Blacklists anlegen'
                : 'Skipped — you can create blacklists anytime'}
          </Typography>
        </Box>
      ) : (
        <Box className={classes.gapBanner}>
          <span className={classes.gapDot}>!</span>
          <Typography variant="caption" className={classes.gapText}>
            {isDe
              ? 'Optional: Blacklists schützen Kunden & laufende Prozesse davor, angeschrieben zu werden.'
              : 'Optional: blacklists protect customers & ongoing deals from being contacted.'}
          </Typography>
        </Box>
      )}

      <Box className={classes.card}>
        <Box className={classes.tableHeader}>
          <Typography variant="caption" className={classes.sectionLabel}>
            {isDe ? 'Blacklists' : 'Blacklists'}
          </Typography>
          <button type="button" className={classes.addButton} onClick={() => setModalOpen(true)}>
            <AddIcon className={classes.addIcon} />
            {isDe ? 'Blacklist anlegen' : 'Create blacklist'}
          </button>
        </Box>

        {lists.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            {isDe
              ? 'Noch keine Blacklist vorhanden. Lege eine an — oder überspringe den Schritt, wenn du keine brauchst.'
              : 'No blacklist yet. Create one — or skip this step if you don’t need any.'}
          </Typography>
        ) : (
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.th} width={34} />
                <TableCell className={classes.th}>{isDe ? 'Name' : 'Name'}</TableCell>
                <TableCell className={classes.th}>{isDe ? 'Typ' : 'Type'}</TableCell>
                <TableCell className={classes.th}>{isDe ? 'Einträge' : 'Entries'}</TableCell>
                <TableCell className={classes.th}>
                  <Box className={classes.thWithInfo}>
                    {isDe ? 'Global' : 'Global'}
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        isDe
                          ? 'Global = gilt automatisch für ALLE Kampagnen und Nutzer des Workspace. Ohne Global-Schalter ist die Liste pro Kampagne wählbar.'
                          : 'Global = automatically applies to ALL campaigns and users of the workspace. Without it, the list is selectable per campaign.'
                      }
                    >
                      <InfoOutlinedIcon className={classes.infoIcon} />
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lists.map((l) => (
                <React.Fragment key={l.id}>
                  <TableRow className={classes.tr} onClick={() => toggleExpand(l)}>
                    <TableCell className={classes.td}>
                      {expandedId === l.id ? (
                        <ExpandMoreIcon className={classes.chevron} />
                      ) : (
                        <ChevronRightIcon className={classes.chevron} />
                      )}
                    </TableCell>
                    <TableCell className={classes.tdName}>{l.name}</TableCell>
                    <TableCell className={classes.td}>
                      {l.type === 'company' ? (isDe ? 'Firmen' : 'Companies') : isDe ? 'Kontakte' : 'Contacts'}
                    </TableCell>
                    <TableCell className={classes.td}>{l.itemCount}</TableCell>
                    <TableCell className={classes.td} onClick={(e) => e.stopPropagation()}>
                      <Switch size="small" checked={l.isDefault} onChange={() => toggleDefault(l)} />
                      <Typography variant="caption" color="textSecondary">
                        {l.isDefault
                          ? isDe ? 'gilt für alle' : 'applies to all'
                          : isDe ? 'pro Kampagne' : 'per campaign'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {expandedId === l.id && (
                    <TableRow>
                      <TableCell colSpan={5} className={classes.expandCell}>
                        <Box className={classes.itemsBox}>
                          {itemsLoading ? (
                            <CircularProgress size={16} />
                          ) : items.length === 0 ? (
                            <Typography variant="caption" color="textSecondary">
                              {isDe ? 'Noch keine Einträge.' : 'No entries yet.'}
                            </Typography>
                          ) : (
                            items.map((it) => (
                              <Box key={it.id} className={classes.itemRow}>
                                <Typography variant="caption" className={classes.itemLabel}>
                                  {it.label}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteItem(l, it.id)}
                                  aria-label={isDe ? 'Eintrag löschen' : 'Delete entry'}
                                >
                                  <DeleteOutlineIcon className={classes.deleteIcon} />
                                </IconButton>
                              </Box>
                            ))
                          )}
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            placeholder={
                              l.type === 'company'
                                ? isDe
                                  ? 'Firmenname oder LinkedIn-Firmen-URL — ein Eintrag pro Zeile'
                                  : 'Company name or LinkedIn company URL — one entry per line'
                                : isDe
                                  ? 'LinkedIn-Profil-URL oder Profilname — ein Eintrag pro Zeile'
                                  : 'LinkedIn profile URL or profile name — one entry per line'
                            }
                            value={newEntries}
                            onChange={(e) => setNewEntries(e.target.value)}
                          />
                          <Box className={classes.itemActions}>
                            <button
                              type="button"
                              className={classes.saveButton}
                              onClick={() => handleAddEntries(l)}
                              disabled={addingItems || newEntries.trim().length === 0}
                            >
                              {addingItems
                                ? isDe ? 'Fügt hinzu…' : 'Adding…'
                                : isDe ? 'Einträge hinzufügen' : 'Add entries'}
                            </button>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
        <Typography variant="caption" color="textSecondary">
          {isDe
            ? 'Liste aufklappen, um Einträge zu sehen und hinzuzufügen (ein Eintrag pro Zeile, auch viele auf einmal) — alternativ geht es auch über den Chat.'
            : 'Expand a list to view and add entries (one per line, many at once) — or do it via the chat.'}
        </Typography>
      </Box>

      {error && (
        <Typography variant="caption" className={classes.error}>
          {error}
        </Typography>
      )}

      {/* Ein Knopf: ohne Liste überspringen, sonst weiter. */}
      <Box className={classes.footer}>
        <Typography variant="caption" className={classes.footerHint}>
          {lists.length === 0
            ? isDe ? 'Keine Blacklist nötig?' : 'No blacklist needed?'
            : isDe ? 'Blacklist angelegt.' : 'Blacklist created.'}
        </Typography>
        {lists.length === 0 && !skipped ? (
          <button type="button" className={classes.skipButton} onClick={onSkip}>
            {isDe ? 'Überspringen' : 'Skip'}
          </button>
        ) : (
          <button type="button" className={classes.saveButton} onClick={onNext}>
            {isDe ? 'Weiter' : 'Next'}
          </button>
        )}
      </Box>

      {/* Modal: Blacklist anlegen */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle className={classes.modalTitle}>
          {isDe ? 'Blacklist anlegen' : 'Create blacklist'}
          <IconButton size="small" onClick={() => setModalOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.modalContent}>
          <TextField
            size="small"
            fullWidth
            label={isDe ? 'Name' : 'Name'}
            placeholder={isDe ? 'z.B. Bestandskunden' : 'e.g. Existing customers'}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            size="small"
            fullWidth
            label={isDe ? 'Beschreibung (optional)' : 'Description (optional)'}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            size="small"
            fullWidth
            select
            label={isDe ? 'Art der Einträge' : 'Entry type'}
            value={type}
            onChange={(e) => setType(e.target.value as 'company' | 'contact')}
            helperText={
              isDe
                ? 'Firmen: sperrt alle Mitarbeiter der Firma. Kontakte: sperrt einzelne Personen.'
                : 'Companies: blocks all employees of the company. Contacts: blocks individual people.'
            }
          >
            <MenuItem value="company">{isDe ? 'Firmen' : 'Companies'}</MenuItem>
            <MenuItem value="contact">{isDe ? 'Kontakte / Kandidaten' : 'Contacts / candidates'}</MenuItem>
          </TextField>
          <Box className={classes.defaultRow}>
            <Switch size="small" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
            <Box>
              <Typography variant="body2" className={classes.defaultLabel}>
                {isDe ? 'Global (für alle Kampagnen und Nutzer)' : 'Global (all campaigns and users)'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {isDe
                  ? 'Ausgeschaltet: die Liste ist pro Kampagne wählbar.'
                  : 'Off: the list is selectable per campaign.'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions className={classes.modalActions}>
          <Button onClick={() => setModalOpen(false)} color="inherit">
            {isDe ? 'Abbrechen' : 'Cancel'}
          </Button>
          <button type="button" className={classes.saveButton} onClick={handleCreate} disabled={!canCreate}>
            {saving ? (isDe ? 'Legt an…' : 'Creating…') : isDe ? 'Anlegen' : 'Create'}
          </button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    maxWidth: 720,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: 32,
  },
  gapBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 14px',
    borderRadius: 12,
    backgroundColor: dc.warnBg,
    border: `1px solid ${dc.warnBorder}`,
  },
  gapDot: {
    flexShrink: 0,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: dc.warn,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    display: 'grid',
    placeItems: 'center',
  },
  gapText: {
    color: dc.warnText,
    lineHeight: 1.4,
  },
  doneBanner: {
    backgroundColor: '#EAFBF4',
    border: '1px solid #C6EEDD',
  },
  doneDot: {
    backgroundColor: dc.success,
  },
  doneText: {
    color: '#0B7050',
  },
  card: {
    backgroundColor: dc.surface,
    border: `1px solid ${dc.border}`,
    borderRadius: 16,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  sectionLabel: {
    fontSize: 11,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: dc.muted,
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    border: `1px solid ${dc.border}`,
    backgroundColor: dc.surface,
    color: dc.primary,
    borderRadius: 10,
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 12.5,
    fontWeight: 700,
    cursor: 'pointer',
    '&:hover': {
      borderColor: dc.primary,
      backgroundColor: dc.primaryHoverBg,
    },
  },
  addIcon: {
    fontSize: '1rem',
  },
  table: {
    '& .MuiTableCell-root': {
      borderBottom: `1px solid ${dc.borderSoft}`,
    },
  },
  th: {
    fontSize: 11,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: dc.faint,
    padding: '6px 10px 8px',
  },
  thWithInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  infoIcon: {
    fontSize: '0.95rem',
    color: dc.faint,
    cursor: 'help',
    '&:hover': {
      color: dc.primary,
    },
  },
  tr: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: dc.bg,
    },
  },
  chevron: {
    fontSize: '1.05rem',
    color: dc.faint,
    verticalAlign: 'middle',
  },
  expandCell: {
    padding: '0 10px 12px 44px !important',
    backgroundColor: dc.bg,
  },
  itemsBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '10px 0 2px',
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    backgroundColor: dc.surface,
    border: `1px solid ${dc.border}`,
    borderRadius: 8,
    padding: '3px 4px 3px 10px',
  },
  itemLabel: {
    color: dc.ink,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  deleteIcon: {
    fontSize: '0.95rem',
    color: dc.faint,
    '&:hover': {
      color: theme.palette.error.main,
    },
  },
  itemActions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  td: {
    fontSize: 13,
    color: dc.muted,
    padding: '8px 10px',
    whiteSpace: 'nowrap',
  },
  tdName: {
    fontSize: 13,
    fontWeight: 600,
    color: dc.ink,
    padding: '8px 10px',
  },
  error: {
    color: theme.palette.error.main,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  footerHint: {
    color: dc.muted,
  },
  skipButton: {
    border: `1px solid ${dc.border}`,
    backgroundColor: dc.surface,
    color: dc.muted,
    borderRadius: 10,
    padding: '8px 16px',
    fontFamily: 'inherit',
    fontSize: 12.5,
    fontWeight: 700,
    cursor: 'pointer',
    '&:hover': {
      borderColor: dc.primary,
      color: dc.primary,
    },
  },
  modalTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontWeight: 700,
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    paddingTop: '8px !important',
  },
  modalActions: {
    padding: '12px 24px 18px',
  },
  defaultRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  defaultLabel: {
    fontWeight: 600,
    color: dc.ink,
  },
  saveButton: {
    flexShrink: 0,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    backgroundColor: dc.primary,
    borderRadius: 10,
    padding: '9px 18px',
    '&:hover': {
      opacity: 0.9,
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'default',
    },
  },
}));

export default BlacklistPanel;
